import type { ChangeEvent, ReactNode } from 'react'
import { Badge, InputGroup, Tooltip } from '@systemeio/components'
import { SlashCircle01Icon } from '@systemeio/icons'
import { EyeIcon, HelpCircleIcon, SearchMdIcon, XIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twJoin } from 'tailwind-merge'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import InviteMembers from 'modules/community/members/components/invite-members'

export type TabKey = 'banned' | 'expired' | 'members' | 'pending'
export const TABS: TabKey[] = ['members', 'pending', 'expired', 'banned']

interface MembersTabsProps {
  activeTab: TabKey
  searchQuery: string
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void
  onClearSearch: () => void
  isUserAllowedToInvite: boolean
  membersCount: number
  pendingCount: number
  expiredCount: number
  bannedCount: number
}

export const MembersTabs = ({
  activeTab,
  searchQuery,
  onSearchChange,
  onClearSearch,
  isUserAllowedToInvite,
  membersCount,
  pendingCount,
  expiredCount,
  bannedCount,
}: MembersTabsProps): ReactNode => {
  // TODO: split this component into 4 separate components: the tab strip, the search bar,
  // the invite-members slot, and the active-tab header/description.
  const { t } = useTranslation()
  const router = useRouter()
  const { data: community } = useCommunityWithTopics()

  const visibleTabs = isUserAllowedToInvite ? TABS : []

  const getTabCount = (tab: TabKey): number => {
    if (tab === 'members') return membersCount
    if (tab === 'pending') return pendingCount
    if (tab === 'expired') return expiredCount
    if (tab === 'banned') return bannedCount
    return 0
  }

  const getTabLabel = (tab: TabKey): string => {
    if (tab === 'members') return t('sidebar.nav.main.members')
    if (tab === 'pending') return t('members.tab.pending_invitations')
    if (tab === 'expired') return t('members.tab.expired_invitations')
    return t('members_banned.filter.title')
  }

  const getTabDescription = (tab: TabKey): string => {
    if (tab === 'members') return t('members.filter.description')
    if (tab === 'pending') return t('members.pending_section.description')
    if (tab === 'expired') return t('members.expired_section.description')
    return t('members_banned.filter.description')
  }

  const handleTabClick = (tab: TabKey): void => {
    void router.replace({ pathname: router.pathname, query: { ...router.query, tab } }, undefined, {
      shallow: true,
    })
  }

  return (
    <div className="mb-6 gap-3 rounded-md bg-bg-primary py-6 md:flex-row md:items-center">
      {visibleTabs.length > 0 && (
        <div className="mb-5 [scrollbar-width:none] overflow-x-auto px-4 xl:px-0 [&::-webkit-scrollbar]:hidden">
          <div className="flex min-w-max justify-evenly gap-4 self-stretch md:min-w-0">
            {visibleTabs.map(tab => {
              const isActive = activeTab === tab
              const isBanned = tab === 'banned'
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    handleTabClick(tab)
                  }}
                  className={twJoin(
                    'flex flex-1 items-center justify-center gap-2 border-b-2 px-2 pt-1 pb-3 text-base font-semibold whitespace-nowrap transition-colors',
                    isActive
                      ? 'border-border-accent text-text-primary'
                      : 'border-transparent text-text-tertiary hover:text-text-primary',
                  )}
                >
                  {isBanned && <SlashCircle01Icon className="size-4" />}
                  <span>{getTabLabel(tab)}</span>
                  {getTabCount(tab) > 0 && (
                    <Badge color="blue" shape="pill">
                      {getTabCount(tab)}
                    </Badge>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="px-4">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:justify-between md:gap-0">
          <div className="w-full md:w-4/6">
            <InputGroup.Root size="md" className="w-full">
              <InputGroup.Input
                data-testid="members-search-input"
                type="search"
                placeholder={t('members.search.placeholder')}
                className="hide-webkit-search-cancel-button"
                value={searchQuery}
                onChange={onSearchChange}
              />
              <InputGroup.Addon align="inline-end" appearance="subtle">
                {searchQuery === '' ? (
                  <SearchMdIcon aria-hidden="true" />
                ) : (
                  <button
                    type="button"
                    data-testid="members-search-clear"
                    onClick={onClearSearch}
                    className="flex size-6 items-center justify-center rounded-sm transition-base"
                  >
                    <XIcon aria-hidden="true" />
                  </button>
                )}
              </InputGroup.Addon>
            </InputGroup.Root>
          </div>
          {community && isUserAllowedToInvite && (
            <InviteMembers communityPath={community.path} isPrivate={community.private} />
          )}
        </div>

        <div>
          <div className="flex items-center gap-2">
            <p className="text-xl font-bold text-text-primary">{getTabLabel(activeTab)}</p>
            <Badge color="blue" shape="pill">
              {getTabCount(activeTab)}
            </Badge>
            {activeTab !== 'members' && (
              <Tooltip
                trigger={<HelpCircleIcon width={16} height={16} className="text-text-tertiary" />}
                content={
                  <div className="flex gap-2">
                    <EyeIcon className="size-4 text-text-secondary" />
                    <p>{t('members.visibility_info.tooltip')}</p>
                  </div>
                }
              />
            )}
          </div>
          <p className="mt-2 text-sm text-text-tertiary">{getTabDescription(activeTab)}</p>
        </div>
      </div>
    </div>
  )
}
